import { useEffect, useState } from "react";
import { RotateCcw } from "lucide-react";
import { toast } from "sonner";
import { SpeciesToggle } from "../SearchForm";
import { Autocomplete } from "./Autocomplete";
import { MyLocationButton, locLabel, locInput } from "./MyLocationButton";
import { REGIONS } from "../../pages/Clinics";

const RADII = [5, 10, 25, 50, 100];
const Label = ({ htmlFor, children }) => <label htmlFor={htmlFor} className="block text-xs font-semibold text-[#52706D] mb-1.5">{children}</label>;
const Check = ({ k, label, params, set }) => (
  <label className="flex items-center gap-2 text-sm text-[#153C3A]">
    <input type="checkbox" className="h-4 w-4 accent-[#087F72]" checked={params[k] === "true"} onChange={(e) => set({ [k]: e.target.checked ? "true" : "" })} data-testid={`filter-${k}`} />{label}
  </label>
);

const toResults = () => setTimeout(() => document.getElementById("compare-results")?.scrollIntoView({ behavior: "smooth" }), 50);

export const FilterPanel = ({ params, set, reset, services, categories, variants }) => {
  const [loc, setLoc] = useState(params.location);
  const [clinic, setClinic] = useState(params.clinic);
  const [range, setRange] = useState({ min: params.price_min, max: params.price_max });
  useEffect(() => { setLoc(params.location); setClinic(params.clinic); setRange({ min: params.price_min, max: params.price_max }); }, [params.location, params.clinic, params.price_min, params.price_max]);
  const types = params.price_type ? params.price_type.split(",") : [];
  const toggleType = (t) => set({ price_type: (types.includes(t) ? types.filter((x) => x !== t) : [...types, t]).join(",") });
  const svcList = params.category ? services.filter((s) => s.category?.code === params.category) : services;
  return (
    <aside className="card-vp p-5 space-y-5" data-testid="compare-filters" aria-label="Filtre">
      <SpeciesToggle value={params.species} onChange={(v) => set({ species: v, variant: "" })} />
      <div>
        <Label htmlFor="f-category">Kategori</Label>
        <select id="f-category" className="field-vp" value={params.category} onChange={(e) => set({ category: e.target.value, service: "", variant: "" })} data-testid="filter-category">
          <option value="">Alle kategorier</option>
          {categories.map((c) => <option key={c.code} value={c.code}>{c.name_da}</option>)}
        </select>
      </div>
      <div>
        <Label htmlFor="f-service">Behandling</Label>
        <select id="f-service" className="field-vp" value={params.service} onChange={(e) => set({ service: e.target.value, variant: "" })} data-testid="compare-treatment-select">
          <option value="">Vælg behandling…</option>
          {svcList.map((s) => <option key={s.id} value={s.slug}>{s.name_da}</option>)}
        </select>
      </div>
      {variants.length > 0 && (
        <div>
          <Label htmlFor="f-variant">Vægtklasse / variant</Label>
          <select id="f-variant" className="field-vp" value={params.variant} onChange={(e) => set({ variant: e.target.value })} data-testid="compare-variant-select">
            {variants.map((v) => <option key={v.code} value={v.code}>{v.name_da}</option>)}
          </select>
          <p className="text-[11px] text-[#52706D] mt-1">Priser sammenlignes kun inden for samme vægtklasse.</p>
        </div>
      )}
      <form onSubmit={(e) => { e.preventDefault(); set({ location: loc.trim(), sort: loc.trim() ? "distance" : "price_asc" }); toResults(); }}>
        <Autocomplete id="f-loc" label="By eller postnummer" value={locLabel(loc)} onChange={(v) => setLoc((p) => locInput(p, v))} groups={["places"]} placeholder="F.eks. 8000 eller Odense" testid="compare-location-input"
          onSelect={(g, v) => { setLoc(v); set({ location: v, sort: "distance" }); toResults(); }} />
        <button className="btn-primary btn-sm mt-2 w-full" type="submit" data-testid="compare-location-submit">Søg område</button>
        <MyLocationButton className="mt-2" testid="compare-my-location-button" onLocate={(c) => { setLoc(c); set({ location: c, sort: "distance" }); toResults(); }} />
      </form>
      <div>
        <Label htmlFor="f-radius">Afstand (luftlinje)</Label>
        <select id="f-radius" className="field-vp" value={params.radius} onChange={(e) => set({ radius: e.target.value })} data-testid="compare-radius-select">
          {RADII.map((r) => <option key={r} value={r}>Inden for {r} km</option>)}
          <option value="0">Hele landet</option>
        </select>
      </div>
      <div>
        <Label htmlFor="f-region">Region</Label>
        <select id="f-region" className="field-vp" value={params.region} onChange={(e) => set({ region: e.target.value })} data-testid="filter-region">
          <option value="">Alle regioner</option>
          {REGIONS.map((r) => <option key={r} value={r}>{r}</option>)}
        </select>
      </div>
      <form onSubmit={(e) => { e.preventDefault(); set({ clinic: clinic.trim() }); }}>
        <Autocomplete id="f-clinic" label="Kliniknavn" value={clinic} onChange={setClinic} groups={["clinics"]} placeholder="Søg klinik" testid="filter-clinic-input"
          onSelect={(g, v, l) => { setClinic(l); set({ clinic: l }); }} />
      </form>
      <form className="grid grid-cols-2 gap-2" onSubmit={(e) => { e.preventDefault(); if (range.min && range.max && Number(range.min) > Number(range.max)) { toast.error("Minimumsprisen skal være lavere end maksimumsprisen"); return; } set({ price_min: range.min, price_max: range.max }); }}>
        <div><Label htmlFor="f-min">Pris fra (kr.)</Label><input id="f-min" type="number" min="0" className="field-vp" value={range.min} onChange={(e) => setRange({ ...range, min: e.target.value })} data-testid="filter-price-min" /></div>
        <div><Label htmlFor="f-max">Pris til (kr.)</Label><input id="f-max" type="number" min="0" className="field-vp" value={range.max} onChange={(e) => setRange({ ...range, max: e.target.value })} data-testid="filter-price-max" /></div>
        <button className="btn-outline btn-sm col-span-2" type="submit" data-testid="filter-price-apply">Anvend prisinterval</button>
      </form>
      <fieldset>
        <legend className="block text-xs font-semibold text-[#52706D] mb-1.5">Pristype</legend>
        <div className="flex flex-wrap gap-2">{[["FIXED", "Fast"], ["FROM", "Fra-pris"], ["RANGE", "Interval"], ["ESTIMATE", "Estimat"]].map(([t, l]) => (
          <button key={t} type="button" aria-pressed={types.includes(t)} onClick={() => toggleType(t)} data-testid={`filter-type-${t}`}
            className={`px-3 h-8 rounded-full text-xs font-semibold border transition-colors ${types.includes(t) ? "bg-[#087F72] text-white border-[#087F72]" : "border-[#D7E7E3] text-[#153C3A] hover:bg-[#E6F4EF]"}`}>{l}</button>
        ))}</div>
      </fieldset>
      <div className="space-y-2">
        <Check k="verified_only" label="Kun bekræftede/verificerede priser" params={params} set={set} />
        <Check k="fixed_only" label="Kun faste priser" params={params} set={set} />
        <Check k="has_booking" label="Klinikker med online booking" params={params} set={set} />
        {!params.service && <Check k="has_prices" label="Kun klinikker med offentliggjorte priser" params={params} set={set} />}
      </div>
      <button type="button" className="btn-outline btn-sm w-full" onClick={reset} data-testid="filter-reset"><RotateCcw className="h-4 w-4" />Nulstil alle filtre</button>
    </aside>
  );
};
